// src/pages/HelpPage.js
// Centre d'aide : liste de la documentation à gauche (toujours visible sur
// desktop), guide sélectionné à droite. Une URL par guide (/help/<slug>) pour
// pouvoir pointer un bouton « Comment ça marche ? » depuis chaque fonctionnalité.
// /help sans slug ouvre le premier guide de la liste.
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ChevronRight, Clock, Lightbulb, Mail, Search } from "lucide-react";

import "./help/helpI18n";
import { helpLang } from "./help/helpI18n";
import { ACCENTS, HELP_TOPICS, getHelpTopic, helpRoute } from "./help/helpTopics";
import { HELP_CONTENT_EN, HELP_CONTENT_FR } from "./help/helpContent";
import {
  Card,
  FaqRow,
  HelpBg,
  SUPPORT_EMAIL,
  SUPPORT_WHATSAPP,
  SearchField,
  SupportFab,
  WhatsAppLogo,
  buildMailtoHref,
  buildWhatsAppHref,
  cn,
} from "./help/helpUi";

const DEFAULT_SLUG = HELP_TOPICS[0].slug;
const FAQ_PREVIEW = 3;

/* Illustration décorative de l'entête (aucun asset externe). */
function HelpIllustration({ className = "" }) {
  return (
    <svg viewBox="0 0 260 150" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="fmHelpBook" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#c4b5fd" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
      </defs>
      <rect x="18" y="52" width="104" height="13" rx="6.5" fill="currentColor" opacity="0.10" />
      <rect x="18" y="76" width="82" height="13" rx="6.5" fill="currentColor" opacity="0.10" />
      <rect x="18" y="100" width="116" height="13" rx="6.5" fill="currentColor" opacity="0.07" />
      <g transform="translate(140 24)">
        <rect x="4" y="16" width="98" height="76" rx="12" fill="url(#fmHelpBook)" />
        <rect x="18" y="4" width="84" height="76" rx="12" fill="#ede9fe" opacity="0.95" />
        <rect x="30" y="24" width="60" height="7" rx="3.5" fill="#a78bfa" opacity="0.75" />
        <rect x="30" y="40" width="46" height="7" rx="3.5" fill="#a78bfa" opacity="0.55" />
        <rect x="30" y="56" width="54" height="7" rx="3.5" fill="#a78bfa" opacity="0.4" />
      </g>
      <circle cx="236" cy="18" r="4" fill="#c4b5fd" />
      <circle cx="146" cy="12" r="3" fill="#f0abfc" />
      <circle cx="250" cy="106" r="3" fill="#c4b5fd" />
    </svg>
  );
}

/* --------------------------- Colonne documentation --------------------------- */
function DocList({ items, activeSlug, title, emptyLabel }) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-black/[0.06] px-4 py-3.5 text-[13px] font-semibold text-neutral-900 dark:border-white/[0.07] dark:text-white">
        {title}
      </div>

      <nav className="p-2">
        {items.length === 0 ? (
          <p className="px-2 py-6 text-center text-xs text-neutral-500 dark:text-white/55">{emptyLabel}</p>
        ) : null}

        {items.map(({ topic, title: itemTitle, tagline }) => {
          const Icon = topic.icon;
          const active = topic.slug === activeSlug;
          return (
            <Link
              key={topic.slug}
              to={helpRoute(topic.slug)}
              aria-current={active ? "page" : undefined}
              className={cn(
                "group flex items-center gap-3 rounded-xl px-2.5 py-2.5 transition",
                active
                  ? "bg-violet-500/10 ring-1 ring-violet-500/25"
                  : "hover:bg-black/[0.03] dark:hover:bg-white/[0.05]"
              )}
            >
              <span
                className={cn(
                  "grid h-9 w-9 shrink-0 place-items-center rounded-lg",
                  active ? ACCENTS[topic.accent] : "bg-black/[0.04] text-neutral-500 dark:bg-white/[0.06] dark:text-white/55"
                )}
              >
                <Icon size={17} />
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    "block truncate text-[13.5px] font-semibold",
                    active ? "text-violet-700 dark:text-violet-300" : "text-neutral-800 dark:text-white/85"
                  )}
                >
                  {itemTitle}
                </span>
                <span className="mt-0.5 block truncate text-[11.5px] text-neutral-500 dark:text-white/50">{tagline}</span>
              </span>
              <ChevronRight
                size={15}
                className={cn(
                  "shrink-0 transition",
                  active ? "text-violet-500" : "text-neutral-300 group-hover:text-violet-400 dark:text-white/25"
                )}
              />
            </Link>
          );
        })}
      </nav>
    </Card>
  );
}

/* ------------------------------ Guide affiché ------------------------------ */
function GuidePanel({ topic, guide, content, t, lang }) {
  const [openFaq, setOpenFaq] = useState(null);
  const [showAllFaq, setShowAllFaq] = useState(false);

  useEffect(() => {
    setOpenFaq(null);
    setShowAllFaq(false);
  }, [topic.slug]);

  const Icon = topic.icon;
  const faq = guide.faq || [];
  const visibleFaq = showAllFaq ? faq : faq.slice(0, FAQ_PREVIEW);

  const related = (guide.related || [])
    .map((slug) => ({ topic: getHelpTopic(slug), title: content[slug]?.title }))
    .filter((r) => r.topic && r.title);

  return (
    <div className="space-y-4">
      <Card className="p-5 sm:p-6">
        {/* Icône + titre sur la même ligne ; le texte et les chips restent en
            pleine largeur dessous, jamais indentés par l'icône. */}
        <div className="flex items-center gap-3.5">
          <span className={cn("grid h-12 w-12 shrink-0 place-items-center rounded-2xl", ACCENTS[topic.accent])}>
            <Icon size={24} />
          </span>
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-400">
              {t("labels.guides")}
            </div>
            <h1 className="text-[22px] font-extrabold leading-tight tracking-tight sm:text-[28px]">{guide.title}</h1>
          </div>
        </div>

        <p className="mt-3 text-sm leading-relaxed text-neutral-600 dark:text-white/65">{guide.intro}</p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {guide.readMin ? (
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-black/[0.03] px-2.5 py-1.5 text-[11px] font-semibold text-neutral-600 dark:bg-white/[0.06] dark:text-white/60">
              <Clock size={13} />
              {t("labels.readMin", { count: guide.readMin })}
            </span>
          ) : null}
          {topic.to ? (
            <Link
              to={topic.to}
              className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-violet-700"
            >
              {t("labels.openFeature")}
              <ArrowRight size={13} />
            </Link>
          ) : null}
        </div>
      </Card>

      {/* En bref */}
      {guide.brief?.length ? (
        <Card className="p-5 sm:p-6">
          <h2 className="text-[15px] font-semibold text-neutral-900 dark:text-white">{t("labels.briefTitle")}</h2>
          <ul className="mt-3 space-y-2.5">
            {guide.brief.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2.5 text-sm text-neutral-700 dark:text-white/75">
                <Check size={16} className="mt-0.5 shrink-0 text-violet-500" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* Étapes clés */}
      {guide.steps?.length ? (
        <Card className="p-5 sm:p-6">
          <h2 className="text-[15px] font-semibold text-neutral-900 dark:text-white">{t("labels.stepsTitle")}</h2>
          <ol className="mt-4 space-y-4">
            {/* Numéro + titre en ligne, texte pleine largeur dessous (non indenté). */}
            {guide.steps.map((step, idx) => (
              <li key={idx}>
                <div className="flex items-center gap-2.5">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-violet-500/12 text-xs font-bold text-violet-600 dark:text-violet-300">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 text-sm font-semibold text-neutral-900 dark:text-white">{step.title}</div>
                </div>
                <p className="mt-1 text-sm leading-relaxed text-neutral-600 dark:text-white/65">{step.text}</p>
              </li>
            ))}
          </ol>
        </Card>
      ) : null}

      {/* Sections détaillées — un guide peut avoir des précisions qui ne sont ni
          une étape ni une question : conditions de fonctionnement, cas d'usage,
          ce qui est synchronisé ou non. Les enfouir dans la FAQ les rendait
          invisibles à qui lit le guide dans l'ordre. */}
      {guide.sections?.length
        ? guide.sections.map((section, idx) => (
            <Card key={idx} className="p-5 sm:p-6">
              <h2 className="text-[15px] font-semibold text-neutral-900 dark:text-white">{section.title}</h2>
              {section.text ? (
                <p className="mt-2 text-sm leading-relaxed text-neutral-600 dark:text-white/65">{section.text}</p>
              ) : null}
              {section.bullets?.length ? (
                <ul className="mt-3 space-y-2.5">
                  {section.bullets.map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed text-neutral-700 dark:text-white/75">
                      <Check size={16} className="mt-0.5 shrink-0 text-violet-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {section.note ? (
                <p className="mt-3 rounded-2xl border border-amber-500/25 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">
                  {section.note}
                </p>
              ) : null}
            </Card>
          ))
        : null}

      {/* Astuces */}
      {guide.tips?.length ? (
        <Card className="border-amber-500/20 bg-amber-50/60 p-5 dark:bg-amber-500/[0.07] sm:p-6">
          <h2 className="flex items-center gap-2 text-[15px] font-semibold text-neutral-900 dark:text-white">
            <Lightbulb size={16} className="text-amber-500" />
            {t("labels.tipsTitle")}
          </h2>
          <ul className="mt-3 space-y-2.5">
            {guide.tips.map((tip, idx) => (
              <li key={idx} className="text-sm leading-relaxed text-neutral-700 dark:text-white/75">
                {tip}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* Questions */}
      {faq.length ? (
        <Card className="overflow-hidden">
          <div className="border-b border-black/[0.06] px-5 py-3.5 text-[15px] font-semibold text-neutral-900 dark:border-white/[0.07] dark:text-white">
            {t("labels.faqTitle")}
          </div>

          {visibleFaq.map((item, idx) => {
            const id = `${topic.slug}-${idx}`;
            return (
              <FaqRow
                key={id}
                id={id}
                question={item.q}
                answer={item.a}
                open={openFaq === id}
                onToggle={(next) => setOpenFaq((prev) => (prev === next ? null : next))}
              />
            );
          })}

          {faq.length > FAQ_PREVIEW ? (
            <button
              type="button"
              onClick={() => setShowAllFaq((v) => !v)}
              className="w-full border-t border-black/[0.06] px-5 py-3 text-left text-[13px] font-semibold text-violet-600 transition hover:bg-violet-50 dark:border-white/[0.07] dark:text-violet-300 dark:hover:bg-white/[0.05]"
            >
              {showAllFaq
                ? lang === "en"
                  ? "Show fewer questions"
                  : "Réduire les questions"
                : lang === "en"
                ? `See all questions (${faq.length})`
                : `Voir toutes les questions (${faq.length})`}
            </button>
          ) : null}
        </Card>
      ) : null}

      {/* À lire ensuite */}
      {related.length ? (
        <Card className="p-5 sm:p-6">
          <h2 className="text-[15px] font-semibold text-neutral-900 dark:text-white">{t("labels.relatedTitle")}</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {related.map(({ topic: rt, title: rTitle }) => {
              const RIcon = rt.icon;
              return (
                <Link
                  key={rt.slug}
                  to={helpRoute(rt.slug)}
                  className="group flex items-center gap-2.5 rounded-xl border border-black/[0.07] px-3 py-2.5 transition hover:border-violet-300 dark:border-white/10 dark:hover:border-violet-400/40"
                >
                  <span className={cn("grid h-8 w-8 shrink-0 place-items-center rounded-lg", ACCENTS[rt.accent])}>
                    <RIcon size={15} />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-neutral-800 dark:text-white/85">
                    {rTitle}
                  </span>
                  <ChevronRight size={15} className="shrink-0 text-neutral-300 group-hover:text-violet-500 dark:text-white/25" />
                </Link>
              );
            })}
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/* ----------------------------------- Page ----------------------------------- */
export default function HelpPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation("help", { useSuspense: false });
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  const lang = helpLang(i18n.language);
  const content = lang === "en" ? HELP_CONTENT_EN : HELP_CONTENT_FR;

  const [query, setQuery] = useState("");
  const [supportOpen, setSupportOpen] = useState(false);
  const q = query.trim().toLowerCase();

  const activeSlug = slug || DEFAULT_SLUG;
  const topic = getHelpTopic(activeSlug);
  const guide = topic ? content[topic.slug] : null;

  // Slug inconnu → on revient au centre d'aide plutôt que d'afficher un trou.
  useEffect(() => {
    if (slug && !getHelpTopic(slug)) navigate("/help", { replace: true });
  }, [slug, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [activeSlug]);

  const items = useMemo(() => {
    const all = HELP_TOPICS.map((it) => ({ topic: it, ...(content[it.slug] || {}) }));
    if (!q) return all;
    return all.filter((it) => {
      const faqText = (it.faq || []).map((f) => `${f.q} ${f.a}`).join(" ");
      // Les sections aussi : sinon une recherche sur « VPS » ou « téléphone »
      // ne remonte pas un guide qui en parle longuement.
      const sectionText = (it.sections || [])
        .map((s) => `${s.title || ""} ${s.text || ""} ${(s.bullets || []).join(" ")} ${s.note || ""}`)
        .join(" ");
      return `${it.title || ""} ${it.tagline || ""} ${it.intro || ""} ${sectionText} ${faqText}`
        .toLowerCase()
        .includes(q);
    });
  }, [content, q]);

  const waHref = buildWhatsAppHref(SUPPORT_WHATSAPP, t("support.prefillWhatsApp"));
  const mailHref = buildMailtoHref(SUPPORT_EMAIL, t("support.subjectEmail"), t("support.prefillEmail"));

  const pageAnim = reduce
    ? {}
    : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.25, ease: "easeOut" } };

  return (
    <div className="relative min-h-[calc(100vh-64px)] bg-neutral-50 text-neutral-900 dark:bg-[#050214] dark:text-white">
      <HelpBg />

      <motion.div
        {...pageAnim}
        className="relative mx-auto w-full max-w-[1280px] px-4 pb-[calc(env(safe-area-inset-bottom)+40px)] pt-6 sm:px-6 lg:px-8"
      >
        {/* ENTÊTE */}
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <h1 className="text-[26px] font-extrabold tracking-tight sm:text-[36px]">
              {lang === "en" ? "Help Center " : "Centre d’Aide "}
              <span className="text-violet-600 dark:text-violet-400">FullMetrix</span>
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-neutral-600 dark:text-white/65">{t("subtitle")}</p>

            <div className="mt-5 max-w-[560px]">
              <SearchField
                value={query}
                onChange={setQuery}
                placeholder={t("searchPlaceholder")}
                actionLabel={<Search size={16} />}
                onSubmit={() => {
                  if (items.length >= 1) navigate(helpRoute(items[0].topic.slug));
                }}
              />
            </div>
          </div>

          <HelpIllustration className="hidden h-[140px] w-[240px] shrink-0 text-violet-900/70 dark:text-white lg:block" />
        </div>

        {/* DOC + GUIDE */}
        <div className="mt-6 grid gap-4 lg:grid-cols-[340px_minmax(0,1fr)] lg:items-start">
          {/* Liste : masquée sur mobile quand un guide est ouvert */}
          <div className={cn("lg:sticky lg:top-4", slug ? "hidden lg:block" : "block")}>
            <DocList
              items={items}
              activeSlug={activeSlug}
              title={lang === "en" ? "Documentation" : "Documentation"}
              emptyLabel={t("labels.noResults")}
            />
          </div>

          {/* Guide : masqué sur mobile tant qu'aucun guide n'est choisi */}
          <div className={cn(slug ? "block" : "hidden lg:block")}>
            {slug ? (
              <button
                type="button"
                onClick={() => navigate("/help")}
                className="mb-3 inline-flex items-center gap-2 rounded-xl border border-black/[0.07] bg-white px-3 py-2 text-[13px] font-semibold text-neutral-700 transition hover:bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.05] dark:text-white/80 lg:hidden"
              >
                <ArrowLeft size={15} />
                {t("labels.backToHelp")}
              </button>
            ) : null}

            {topic && guide ? (
              <GuidePanel topic={topic} guide={guide} content={content} t={t} lang={lang} />
            ) : (
              <Card className="p-8 text-center text-sm text-neutral-600 dark:text-white/70">{t("labels.notFound")}</Card>
            )}
          </div>
        </div>

        {/* PIED : assistance */}
        <Card className="mt-5 p-4 sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-neutral-900 dark:text-white">{t("support.blockTitle")}</div>
              <p className="mt-0.5 text-xs text-neutral-500 dark:text-white/55">{t("support.blockDesc")}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <a
                href={waHref || "#"}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => !waHref && e.preventDefault()}
                className="inline-flex items-center gap-2 rounded-xl border border-black/[0.07] bg-white px-3.5 py-2.5 text-[13px] font-semibold text-neutral-700 transition hover:border-emerald-300 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/80"
              >
                <WhatsAppLogo className="text-emerald-600" size={16} />
                WhatsApp
              </a>
              <a
                href={mailHref || "#"}
                onClick={(e) => !mailHref && e.preventDefault()}
                className="inline-flex items-center gap-2 rounded-xl border border-black/[0.07] bg-white px-3.5 py-2.5 text-[13px] font-semibold text-neutral-700 transition hover:border-violet-300 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/80"
              >
                <Mail size={16} className="text-violet-600" />
                Email
              </a>
              {/* Ouvre la même pop-up que le bouton flottant en bas à droite. */}
              <button
                type="button"
                onClick={() => setSupportOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-3.5 py-2.5 text-[13px] font-semibold text-white transition hover:bg-violet-700"
              >
                {t("support.title")}
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </Card>
      </motion.div>

      <SupportFab t={t} open={supportOpen} onOpenChange={setSupportOpen} />
    </div>
  );
}
